import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, Search, MapPin, Calendar, ExternalLink, Bell } from "lucide-react";

const RANGES = [
  { key: "upcoming", label: "Upcoming" },
  { key: "today", label: "Today" },
  { key: "next7", label: "Next 7 days" },
  { key: "past", label: "Past" },
  { key: "all", label: "All" },
];
const STATUSES = [
  { key: "", label: "Any status" },
  { key: "proposed", label: "Proposed" },
  { key: "confirmed", label: "Confirmed" },
  { key: "confirmed_change_proposed", label: "Change proposed" },
  { key: "cancelled", label: "Cancelled" },
];

const badge = (s) => ({
  proposed: "bg-amber-100 text-amber-700", confirmed: "bg-emerald-100 text-emerald-700",
  confirmed_change_proposed: "bg-amber-100 text-amber-700", cancelled: "bg-zinc-200 text-zinc-600",
}[s] || "bg-zinc-100 text-zinc-700");

const ukDateTime = (utc) => utc ? new Date(utc).toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export function AdminVisits() {
  const [range, setRange] = useState("upcoming");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/visits", { params: { range, status, q } })
      .then((r) => setRows(r.data.visits)).finally(() => setLoading(false));
  }, [range, status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="admin-visits">
      <div className="flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <button key={r.key} data-testid={`visits-range-${r.key}`} onClick={() => setRange(r.key)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${range === r.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 border border-zinc-200"}`}>{r.label}</button>
        ))}
        <select data-testid="visits-status" value={status} onChange={(e) => setStatus(e.target.value)} className="ml-auto rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm">
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
      <div className="mt-3 relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <Input data-testid="visits-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business, creator or opportunity" className="h-11 rounded-full pl-9" />
      </div>

      {loading ? <div className="mt-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div> : (
        <div className="mt-4 space-y-3">
          {rows.length === 0 ? <p className="text-sm text-zinc-400">No visits match these filters.</p> :
            rows.map((v) => (
              <div key={v.collaboration_id} data-testid={`visit-row-${v.collaboration_id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display font-bold text-zinc-900">{v.opportunity_title || "Collaboration"}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge(v.status)}`}>{v.status.replace(/_/g, " ")}</span>
                  {v.is_pending && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-600">awaiting confirmation</span>}
                </div>
                <p className="mt-1 flex items-center gap-2 text-sm text-zinc-700"><Calendar className="h-4 w-4 text-zinc-400" /> {ukDateTime(v.start_utc)} <span className="text-xs text-zinc-400">({v.timezone})</span></p>
                {v.location && <p className="mt-1 flex items-center gap-1 text-sm text-zinc-500"><MapPin className="h-3.5 w-3.5" /> {v.location}</p>}
                <p className="mt-1 text-sm text-zinc-500"><b>Business:</b> {v.business.name} · <b>Creator:</b> {v.creator.name}</p>
                {v.reminders?.length > 0 && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-zinc-400"><Bell className="h-3 w-3" /> Reminders: {v.reminders.map((r) => r.status).join(", ")}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  <a href={v.collab_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-emerald-700 hover:underline"><ExternalLink className="h-3 w-3" /> Open collaboration</a>
                </div>
              </div>
            ))}
        </div>
      )}
      <p className="mt-4 text-xs text-zinc-400">Read-only. A past date does not mean the visit took place or the collaboration completed.</p>
    </div>
  );
}
